import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Filter, Search } from 'lucide-react';
import { Botao } from '@/componentes/ui/botao';
import { cn, numero } from '@/lib/utils';

export interface OpcaoFiltro {
  valor: string;
  qtd: number;
}

/** Acima disto a lista pede para pesquisar: 10 mil caixas travam a tela. */
const MAX_VISIVEIS = 500;

/**
 * Filtro de coluna como o do Excel: pesquisa, "selecionar tudo" e uma caixa
 * por valor que existe na coluna, com quantas linhas têm aquele valor.
 *
 * As opções vêm de quem chama já filtradas pelas OUTRAS colunas — escolher a
 * zona PP e depois abrir Corredor mostra só os corredores da PP, como no Excel.
 * `selecionados === null` é "sem filtro" (tudo marcado).
 */
export function FiltroColuna({
  titulo,
  opcoes,
  selecionados,
  aoAplicar,
}: {
  titulo: string;
  /** Chamada só ao abrir: montar as opções de 9 colunas a cada render é desperdício. */
  opcoes: () => OpcaoFiltro[];
  selecionados: readonly string[] | null;
  aoAplicar: (valores: string[] | null) => void;
}): JSX.Element {
  const [aberto, definirAberto] = React.useState(false);
  const [lista, definirLista] = React.useState<OpcaoFiltro[]>([]);
  const [rascunho, definirRascunho] = React.useState<Set<string>>(new Set());
  const [pesquisa, definirPesquisa] = React.useState('');
  const ativo = selecionados !== null;

  const abrir = (v: boolean): void => {
    if (v) {
      const todas = opcoes();
      definirLista(todas);
      definirRascunho(new Set(selecionados ?? todas.map((o) => o.valor)));
      definirPesquisa('');
    }
    definirAberto(v);
  };

  const termo = pesquisa.trim().toLowerCase();
  const casam = termo ? lista.filter((o) => o.valor.toLowerCase().includes(termo)) : lista;
  const visiveis = casam.slice(0, MAX_VISIVEIS);
  const todasMarcadas = casam.length > 0 && casam.every((o) => rascunho.has(o.valor));

  const alternar = (valor: string): void =>
    definirRascunho((r) => {
      const n = new Set(r);
      if (n.has(valor)) n.delete(valor);
      else n.add(valor);
      return n;
    });

  const alternarTodas = (): void =>
    definirRascunho((r) => {
      const n = new Set(r);
      for (const o of casam) {
        if (todasMarcadas) n.delete(o.valor);
        else n.add(o.valor);
      }
      return n;
    });

  const aplicar = (): void => {
    // Com pesquisa, vale o que o Excel faz: só os valores que casaram e estão marcados.
    const escolhidos = casam.filter((o) => rascunho.has(o.valor)).map((o) => o.valor);
    const tudo = !termo && escolhidos.length === lista.length;
    aoAplicar(tudo ? null : escolhidos);
    definirAberto(false);
  };

  return (
    <Popover.Root open={aberto} onOpenChange={abrir}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`Filtrar ${titulo}`}
          title={ativo ? `Filtrando ${titulo}` : `Filtrar ${titulo}`}
          className={cn(
            'grid h-6 w-6 shrink-0 place-items-center rounded transition-colors',
            ativo ? 'bg-primaria text-white' : 'text-textoFraco/60 hover:bg-superficie hover:text-primaria',
          )}
        >
          <Filter className="h-3 w-3" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-50 flex w-64 flex-col gap-2 rounded-xl border border-borda bg-superficie p-2.5 text-[13px] shadow-forte"
        >
          <label className="flex items-center gap-2 rounded-md border border-borda bg-superficie2 px-2">
            <Search className="h-3.5 w-3.5 text-textoFraco" />
            <input
              autoFocus
              value={pesquisa}
              onChange={(e) => definirPesquisa(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && aplicar()}
              placeholder="Pesquisar"
              className="h-8 w-full bg-transparent outline-none"
            />
          </label>
          <div className="max-h-64 overflow-y-auto rounded-md border border-borda">
            <label className="flex cursor-pointer items-center gap-2 border-b border-borda bg-superficie2 px-2 py-1.5 font-semibold">
              <input type="checkbox" checked={todasMarcadas} onChange={alternarTodas} />
              {termo ? '(Selecionar resultados)' : '(Selecionar tudo)'}
            </label>
            {visiveis.map((o) => (
              <label key={o.valor} className="flex cursor-pointer items-center gap-2 px-2 py-1 hover:bg-superficie2">
                <input type="checkbox" checked={rascunho.has(o.valor)} onChange={() => alternar(o.valor)} />
                <span className="min-w-0 flex-1 truncate">{o.valor === '' ? '(vazio)' : o.valor}</span>
                <span className="tabular text-[11px] text-textoFraco">{numero(o.qtd)}</span>
              </label>
            ))}
            {casam.length > MAX_VISIVEIS ? (
              <p className="px-2 py-1.5 text-[11px] text-textoFraco">
                Mostrando {numero(MAX_VISIVEIS)} de {numero(casam.length)}. Pesquise para achar o resto.
              </p>
            ) : null}
            {casam.length === 0 ? <p className="px-2 py-1.5 text-[11px] text-textoFraco">Nada encontrado.</p> : null}
          </div>
          <div className="flex justify-end gap-1.5">
            {ativo ? (
              <Botao
                variante="fantasma"
                tamanho="micro"
                onClick={() => {
                  aoAplicar(null);
                  definirAberto(false);
                }}
              >
                Limpar filtro
              </Botao>
            ) : null}
            <Botao
              variante="primaria"
              tamanho="micro"
              onClick={aplicar}
              disabled={!casam.some((o) => rascunho.has(o.valor))}
            >
              OK
            </Botao>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
